import { logger } from '@accedo/logger';
import { ConfigService } from '##/services/elevate/types';
import { ApplicationConfig, ElevateConfig, FeatureFlagsConfig, ProvidersConfig } from '##/dataModels/config';
import { getSession, getAllMetadata, getAllAssets, getApplicationStatus, type Options } from '##/services/nzos';

// Legacy metadata structure from Accedo Control (kept for backward compatibility)
interface ElevateMetadata {
  configName: string;
  application: ApplicationConfig;
  featureFlags: FeatureFlagsConfig;
  providers: ProvidersConfig;
}
const PUBLIC_PATH = process.env.PUBLIC_PATH || '/';
const CONFIG_EXPIRY = 1000 * 60 * 60; // 1 hour cache
const CONFIG_KEY_EXPIRY = `CCS_E_${PUBLIC_PATH}`;
const CONFIG_KEY = `CCS_C_${PUBLIC_PATH}`;
const LP = '[ControlConfigService]';

export class ControlConfigService implements ConfigService {
  // eslint-disable-next-line no-use-before-define
  private static instance?: ControlConfigService;
  // eslint-disable-next-line @typescript-eslint/no-empty-function, no-empty-function
  private constructor() {}

  static getInstance(): ControlConfigService {
    if (!ControlConfigService.instance) {
      ControlConfigService.instance = new ControlConfigService();
    }

    return ControlConfigService.instance;
  }

  // eslint-disable-next-line class-methods-use-this
  async getConfig(segment?: string): Promise<ElevateConfig> {
    const configExpiry: string | null = sessionStorage.getItem(CONFIG_KEY_EXPIRY);
    const currentTime = Date.now();
    if (configExpiry && currentTime < Number(configExpiry)) {
      logger.debug(`${LP} Using cached Elevate configuration`);
      const cachedConfig = sessionStorage.getItem(CONFIG_KEY);
      if (cachedConfig) {
        return JSON.parse(cachedConfig) as ElevateConfig;
      }
    }
    // make sure we have a valid session
    const options: Options = { gid: segment };
    await getSession(options);
    // NZOS API returns the complete config structure directly
    // No need for separate metadata/assets/status calls
    const [nzosConfig, metadata, status] = await Promise.all([
      getAllAssets(options), // This gets the full NZOS config
      getAllMetadata(options), // Legacy metadata for backward compatibility
      getApplicationStatus(options),
    ]);

    logger.debug(`${LP} NZOS Config received:`, nzosConfig);
    logger.debug(`${LP} Legacy Metadata received:`, metadata);
    logger.debug(`${LP} Status received:`, status);

    // Extract NZOS config fields
    const configData = nzosConfig as any;
    const { featureFlags, providers, application } = (metadata as unknown as ElevateMetadata) || {};

    // Parse feature flags from NZOS API
    const { appIdGidEasterEggActive, debugOverlayVisible } = configData.featureFlags || featureFlags || {};
    const parsedFeatureFlags = {
      appIdGidEasterEggActive: appIdGidEasterEggActive ?? false,
      debugOverlayVisible: debugOverlayVisible ?? false,
    };

    // Build config using NZOS API structure
    const config = {
      status: configData.status || status?.status,
      appLogo: configData.appLogo,
      landingPageBackdrop: configData.landingPageBackdrop,
      featureFlags: parsedFeatureFlags,
      settings: configData.settings,
      search: configData.search,
      registrationUrl: configData.registrationUrl,
      forgotPasswordUrl: configData.forgotPasswordUrl,
      // Include legacy fields for backward compatibility (providers, application)
      providers: configData.providers || providers,
      application: configData.application || application,
    } as ElevateConfig;

    // Only cache config if it's valid (not error status)
    if (config.status !== 'error') {
      sessionStorage.setItem(CONFIG_KEY, JSON.stringify(config));
      sessionStorage.setItem(CONFIG_KEY_EXPIRY, String(currentTime + CONFIG_EXPIRY));
    } else {
      // Clear any existing cached error config
      sessionStorage.removeItem(CONFIG_KEY);
      sessionStorage.removeItem(CONFIG_KEY_EXPIRY);
      logger.error(`${LP} Config has error status, not caching`);
    }

    return config;
  }
}
